import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FIELD_TYPES } from "../custom-component/fieldHelpers";

/**
 * AccordionManager
 *
 * Renders a list of accordion items dynamically from `items` prop (typically from DB json/jsonb like `faq_data`).
 *
 * ------------------------------------------------------------------
 * ✅ Expected `items` prop / DB json shape (faq_data)
 * ------------------------------------------------------------------
 * items = [
 *   {
 *     id: "faq1",                     // required (string) unique
 *     title: "What is this service?", // required (string) accordion header
 *     fields: [                       // optional (array) accordion content blocks
 *       { type: "text", value: "Answer text...", variant: "body1" },
 *       { type: "divider" },
 *       { type: "list", ordered: false, items: ["Point A", "Point B"] },
 *       { type: "rich_list", ordered: true, items: [...] },
 *       { type: "link", href: "https://example.com", text: "Read more" },
 *       { type: "html", value: "<b>Trusted HTML only</b>" }
 *     ]
 *   }
 * ]
 *
 */
const AccordionManager = ({
  title = "FAQ",
  items = [],
  defaultExpandedId = null,
  containerSx = {},
  titleSx = {},
  accordionSx = {},
}) => {
  const [t] = useTranslation();

  // #region RICH_LIST_STYLES
  // NOTE:
  // - This supports multi-layer lists with custom text styling coming from DB.
  // - For safety, DB-provided sx is sanitized (whitelist keys) before applying.
  // - Prefer using `style` tokens from DB (e.g. "listTitle", "listBody").
  const TEXT_PRESETS = useMemo(
    () => ({
      listTitle: { fontWeight: 700 },
      listBody: { fontWeight: 400 },
      muted: { color: "text.secondary" },
      success: { color: "#1f7a54", fontWeight: 700 },
    }),
    []
  );

  const sanitizeSx = useCallback((sx) => {
    if (!sx || typeof sx !== "object" || Array.isArray(sx)) return undefined;

    const ALLOWED_KEYS = new Set([
      // spacing
      "m", "mt", "mb", "ml", "mr", "mx", "my",
      "p", "pt", "pb", "pl", "pr", "px", "py",
      // typography
      "fontWeight", "fontSize", "lineHeight", "color",
      "textAlign", "textDecoration", "textTransform", "whiteSpace",
    ]);

    return Object.fromEntries(Object.entries(sx).filter(([k]) => ALLOWED_KEYS.has(k)));
  }, []);

  const renderTextRuns = useCallback(
    (runs = []) => {
      if (!Array.isArray(runs) || runs.length === 0) return null;

      return runs.map((run, i) => {
        const preset = run?.style ? TEXT_PRESETS[run.style] : undefined;
        const safeSx = sanitizeSx(run?.sx);

        return (
          <Typography
            key={`${run?.id || i}`}
            component="span"
            variant={run?.variant || "body1"}
            sx={{ ...(preset || {}), ...(safeSx || {}) }}
          >
            {t(run?.text ?? "")}
          </Typography>
        );
      });
    },
    [TEXT_PRESETS, sanitizeSx, t]
  );

  const renderRichList = useCallback(
    (list, keyPrefix = "rich") => {
      if (!list) return null;

      const ordered = !!list?.ordered;
      const Tag = ordered ? "ol" : "ul";
      const nodes = Array.isArray(list?.items) ? list.items : [];
      if (!nodes.length) return null;

      const listSx = sanitizeSx(list?.sx);

      return (
        <Box component={Tag} sx={{ m: 0, pl: 3, ...(listSx || {}) }}>
          {nodes.map((node, idx) => {
            const nodeKey = node?.id || `${keyPrefix}-${idx}`;
            const nodeSx = sanitizeSx(node?.sx);

            if (typeof node === "string") {
              return (
                <li key={nodeKey}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {t(node)}
                  </Typography>
                </li>
              );
            }

            const runs = Array.isArray(node?.runs)
              ? node.runs
              : node?.text
              ? [{ text: node.text, style: "listBody" }]
              : [];

            const children = Array.isArray(node?.children) ? node.children : [];

            return (
              <li key={nodeKey}>
                <Box sx={{ ...(nodeSx || {}) }}>
                  <Typography component="div" variant="body1" sx={{ lineHeight: 1.7 }}>
                    {renderTextRuns(runs)}
                  </Typography>

                  {children.map((childList, cIdx) => (
                    <Box key={`${nodeKey}-c-${cIdx}`} sx={{ mt: 0.5 }}>
                      {renderRichList(childList, `${nodeKey}-c-${cIdx}`)}
                    </Box>
                  ))}
                </Box>
              </li>
            );
          })}
        </Box>
      );
    },
    [renderTextRuns, sanitizeSx, t]
  );
  // #endregion

  const safeDefaultExpanded = useMemo(() => {
    if (!items?.length) return false;
    if (defaultExpandedId && items.some((i) => i.id === defaultExpandedId)) return defaultExpandedId;
    return false;
  }, [items, defaultExpandedId]);

  const [expanded, setExpanded] = useState(safeDefaultExpanded);

  useEffect(() => {
    if (!items?.length) return;
    if (expanded && !items.some((i) => i.id === expanded)) {
      setExpanded(false);
    }
  }, [items, expanded]);

  const handleChange = useCallback(
    (panelId) => (_e, isExpanded) => {
      setExpanded(isExpanded ? panelId : false);
    },
    []
  );

  const renderField = (field, idx) => {
    const type = field?.type || FIELD_TYPES.TEXT;
    const key = field?.id || field?.name || `${type}-${idx}`;
    
    switch (type) {
      case FIELD_TYPES.DIVIDER:
        return <Divider key={key} sx={{ my: 1, ...(field?.sx || {}) }} />;

      case FIELD_TYPES.RICH_LIST: {
        // Multi-layer list with styled text runs (from DB)
        // Shape: { type: 'rich_list', ordered: true/false, items: [...], sx?: {} }
        // Each item node: { runs: [{ text, style?, sx? }], children?: [ { ordered, items }... ], sx?: {} }
        return (
          <Box key={key} sx={field?.sx}>
            {!!field?.label && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            )}

            {renderRichList(field, key)}
          </Box>
        );
      }

      case FIELD_TYPES.LINK: {
        const href = field?.href || "#";
        const displayText = t(field?.text || field?.value || href || "Open");

        return (
          <Box key={key} sx={field?.sx}>
            {!!field?.value && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.value)}
              </Typography>
            )}

            <MuiLink href={href} target="_blank" rel="noopener noreferrer">
              {displayText}
            </MuiLink>
          </Box>
        );
      }

      case FIELD_TYPES.LIST: {
        const itemsList = field?.items || [];
        if (!itemsList.length) return null;

        const Tag = field?.ordered ? "ol" : "ul";

        return (
          <Box key={key} sx={field?.sx}>
            {!!field?.label && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            )}

            <Box component={Tag} sx={{ m: 0, pl: 3 }}>
              {itemsList.map((x, i) => (
                <li key={`${key}-${i}`}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {t(x)}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        );
      }

      case FIELD_TYPES.HTML:
        return (
          <Box
            key={key}
            sx={field?.sx}
            dangerouslySetInnerHTML={{ __html: t(field?.value || "") }}
          />
        );

      case FIELD_TYPES.TEXT:
      default:
        return (
          <Box key={key} sx={field?.sx}>
            {!!field?.label && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t(field.label)}
              </Typography>
            )}

            <Typography
              variant={field?.variant || "body1"}
              sx={{ lineHeight: 1.7, fontWeight: field?.fontWeight }}
            >
              {t(field?.value) ?? ""}
            </Typography>
          </Box>
        );
    }
  };

  if (!items?.length) return null;

  return (
    <Box sx={{ width: "100%", ...containerSx }}>
      {!!title && (
        <Typography variant="h5" sx={{ mb: 1, ...titleSx }}>
          {t(title)}
        </Typography>
      )}

      {items.map((item) => (
        <Accordion
          key={item.id}
          expanded={expanded === item.id}
          onChange={handleChange(item.id)}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            ...accordionSx,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(item.title)}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {(item?.fields || []).map((f, idx) => renderField(f, idx))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default memo(AccordionManager);
